import { useEffect, useState } from "react";

import {
  type ContentAccess,
  type FindingView,
  type ObservationView,
  type RegionDetail,
  type TextExcerpt,
  EXCERPT_BYTES,
  describeLocator,
  getTextExcerpt,
  originalUrl,
  overlayBox,
  pageImageUrl,
} from "./explorerClient";

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

export function Withheld({ access }: { access: ContentAccess }) {
  return (
    <p className="se-withheld" data-testid="content-withheld">
      Content withheld ({access.access_classification}, preview policy {access.policy_version}):{" "}
      {access.reason}
    </p>
  );
}

function OriginalText({
  versionId,
  range,
  rangeKey,
}: {
  versionId: string;
  range: [number, number] | null;
  rangeKey: string | null;
}) {
  // undefined: open at the region (or the file's start when no range is recorded).
  const [offset, setOffset] = useState<number | undefined>(undefined);
  const [excerpt, setExcerpt] = useState<TextExcerpt | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getTextExcerpt(versionId, { offset, range })
      .then((value) => active && setExcerpt(value))
      .catch((caught: unknown) => active && setError(message(caught)));
    return () => {
      active = false;
    };
  }, [versionId, offset, range]);

  if (error) return <p role="alert">{error}</p>;
  if (!excerpt) return <p>Loading the original text…</p>;
  const mark = excerpt.mark;
  const end = excerpt.offset + excerpt.length;
  return (
    <figure>
      <figcaption>
        Original bytes {excerpt.offset}–{end} of {excerpt.byte_size}, decoded as UTF-8 (verbatim;
        undecodable bytes shown as �){excerpt.truncated ? " — more follows" : ""}.{" "}
        {range
          ? mark
            ? `The highlighted text is ${rangeKey === null ? "this region" : rangeKey} (bytes ${range[0]}–${range[1]}).`
            : `This region is bytes ${range[0]}–${range[1]}, outside this window.`
          : "Its parser records no byte position for this region; page through the original and use the locator above."}
      </figcaption>
      <pre className="se-text" data-testid="original-text">
        {mark ? (
          <>
            {excerpt.text.slice(0, mark[0])}
            <mark data-testid="region-mark">{excerpt.text.slice(mark[0], mark[1])}</mark>
            {excerpt.text.slice(mark[1])}
          </>
        ) : (
          excerpt.text
        )}
      </pre>
      <nav aria-label="Original text pages">
        <button
          type="button"
          disabled={excerpt.offset === 0}
          onClick={() => setOffset(Math.max(0, excerpt.offset - EXCERPT_BYTES))}
        >
          Earlier bytes
        </button>
        <button type="button" disabled={!excerpt.truncated} onClick={() => setOffset(end)}>
          Later bytes
        </button>
        {range && offset !== undefined && (
          <button type="button" onClick={() => setOffset(undefined)}>
            Back to this region
          </button>
        )}
      </nav>
    </figure>
  );
}

/** The exact place in the original: the rendered page or frame with the region's box on it,
 * the verbatim text, or the locator alone when the format has no inline preview. */
function OriginalPreview({ detail }: { detail: RegionDetail }) {
  const view = detail.source_view;
  const versionId = detail.source_artifact_version_id;
  if (!detail.content_access.allowed) return <Withheld access={detail.content_access} />;
  if (view.kind === "page_image") {
    const box = overlayBox(view.frame_bbox, view.region_bbox);
    return (
      <figure>
        <figcaption>
          {view.frame_key ? `Page ${view.page} (${view.frame_key})` : `Page ${view.page}`} rendered
          from the original{box ? "; the outlined box is this region" : ""}
        </figcaption>
        <div className="se-page">
          <img
            src={pageImageUrl(versionId, view.page)}
            alt={`Page ${view.page} of the original`}
            data-testid="page-image"
          />
          {box && (
            <div
              className="se-box"
              data-testid="region-box"
              style={{
                left: `${box.left}%`,
                top: `${box.top}%`,
                width: `${box.width}%`,
                height: `${box.height}%`,
              }}
            />
          )}
        </div>
      </figure>
    );
  }
  if (view.kind === "text") {
    return (
      <OriginalText
        key={`${versionId}:${detail.region_key}`}
        versionId={versionId}
        range={view.byte_range}
        rangeKey={view.range_key}
      />
    );
  }
  return <p>{view.reason}</p>;
}

function FindingItem({
  finding,
  onNavigate,
}: {
  finding: FindingView;
  onNavigate: (anchor: string) => void;
}) {
  return (
    <li className={`se-finding se-${finding.severity}`}>
      <strong>{finding.finding_code}</strong> ({finding.severity}
      {finding.action !== "none" ? `, ${finding.action}s this region` : ""})
      {finding.message !== null ? `: ${finding.message}` : ""}
      {finding.region_key && finding.on_this_region === false && (
        <>
          {" "}
          on{" "}
          <button type="button" className="se-link" onClick={() => onNavigate(finding.region_key ?? "")}>
            {finding.region_key}
          </button>
        </>
      )}
      {!finding.region_key && " (whole document)"}
    </li>
  );
}

function value(observation: ObservationView): string {
  if (observation.mode === "transcription") return observation.transcribed_text ?? "";
  const shown = JSON.stringify(observation.interpreted_value);
  return `${shown ?? ""}${observation.unit_text ? ` ${observation.unit_text}` : ""}`;
}

function Observation({ observation, allowed }: { observation: ObservationView; allowed: boolean }) {
  return (
    <li data-testid={`observation-${observation.representation}`}>
      <span className={`se-tag se-tag-${observation.representation}`}>
        {observation.representation === "transcribed" ? "Transcription" : "Interpretation"}
      </span>{" "}
      {observation.ai_proposal && (
        <span className="se-tag se-tag-ai" data-testid="ai-proposal">
          AI proposal — not evidence
        </span>
      )}{" "}
      {observation.interpretation_kind && <em>{observation.interpretation_kind} </em>}
      by {observation.producer_kind} <code>{observation.producer_ref}</code>
      {observation.transformation && ` · ${observation.transformation}`}
      {allowed ? <div className="se-value">{value(observation)}</div> : null}
    </li>
  );
}

export function RegionPanel({
  detail,
  onNavigate,
}: {
  detail: RegionDetail;
  onNavigate: (anchor: string) => void;
}) {
  const allowed = detail.content_access.allowed;
  const locator = describeLocator(detail.locator);
  const transcribed = detail.observations.filter((o) => o.representation === "transcribed");
  const interpreted = detail.observations.filter((o) => o.representation === "interpreted");
  return (
    <article aria-label={`Region ${detail.region_key}`}>
      <nav aria-label="Anchor path" className="se-breadcrumb">
        {detail.ancestors.map((a) => (
          <span key={a.region_key}>
            <button type="button" className="se-link" onClick={() => onNavigate(a.region_key)}>
              {a.region_key}
            </button>{" "}
            /{" "}
          </span>
        ))}
        <strong>{detail.region_key}</strong>
      </nav>
      <p data-testid="region-anchor">
        Anchor <code>{detail.region_key}</code> · {detail.region_kind}
        {detail.is_hidden ? " · hidden in the source" : ""}
        {locator ? ` · ${locator}` : ""}
      </p>

      <section className="se-pane se-original" data-representation="original" aria-label="Original">
        <header>
          <span className="se-tag se-tag-original">Original</span> source of record: the vault
          bytes, at this region's location
        </header>
        <OriginalPreview detail={detail} />
        {allowed && (
          <p>
            <a href={originalUrl(detail.source_artifact_version_id)} download>
              Download the verified original
            </a>
          </p>
        )}
      </section>

      <section className="se-pane se-parsed" data-representation="parsed" aria-label="Parsed">
        <header>
          <span className="se-tag se-tag-parsed">Parsed</span> canonical parse by{" "}
          {detail.parsed.parser}
        </header>
        {detail.parsed.withheld ? (
          <Withheld access={detail.content_access} />
        ) : (
          <dl>
            {detail.parsed.text_content !== null && (
              <>
                <dt>Text</dt>
                <dd data-testid="parsed-text">{detail.parsed.text_content}</dd>
              </>
            )}
            {detail.parsed.displayed_value !== null && (
              <>
                <dt>Displayed value</dt>
                <dd>{detail.parsed.displayed_value}</dd>
              </>
            )}
            {detail.parsed.formula !== null && (
              <>
                <dt>Formula</dt>
                <dd>
                  <code>{detail.parsed.formula}</code>
                </dd>
              </>
            )}
          </dl>
        )}
        {Object.keys(detail.parsed.attributes).length > 0 && (
          <details>
            <summary>Attributes</summary>
            <pre>{JSON.stringify(detail.parsed.attributes, null, 2)}</pre>
          </details>
        )}
        <p data-testid="region-quality" className={`se-${detail.quality.action}`}>
          Extraction quality: {detail.quality.condition}
          {detail.quality.action !== "none" ? ` · ${detail.quality.action}` : ""} (not an
          evidence grade)
        </p>
        {detail.findings.length > 0 && (
          <ul aria-label="Findings">
            {detail.findings.map((f) => (
              <FindingItem key={f.finding_id} finding={f} onNavigate={onNavigate} />
            ))}
          </ul>
        )}
      </section>

      <section
        className="se-pane se-interpreted"
        data-representation="interpreted"
        aria-label="Interpreted"
      >
        <header>
          <span className="se-tag se-tag-interpreted">Interpreted</span> observations made from
          this region
        </header>
        {detail.observations.length === 0 ? (
          <p>No observations recorded for this region.</p>
        ) : (
          <>
            {!allowed && <Withheld access={detail.content_access} />}
            <ul aria-label="Observations">
              {[...transcribed, ...interpreted].map((o) => (
                <Observation key={o.observation_id} observation={o} allowed={allowed} />
              ))}
            </ul>
          </>
        )}
      </section>
    </article>
  );
}
